import { createClient } from '@connectrpc/connect';
import { create } from '@bufbuild/protobuf';
import {
  BranchKind,
  ListCommitsRequestSchema,
  VersionControlService,
  type BranchInfo,
  type CommitInfo,
} from '@puppycad/proto';
import { grpcWebTransport } from '@/services/api/transport/grpc-web-transport';
import type {
  FetchVersionRecordsOptions,
  FetchVersionSnapshotOptions,
  VersionPreviewRecord,
  VersionPreviewService,
  VersionPreviewSnapshot,
} from '../types/version-preview-types';

const DEFAULT_VERSION_PREVIEW_PAGE_SIZE = 500;

const versionClient = createClient(VersionControlService, grpcWebTransport);

export interface VersionPreviewRepositoryContext {
  /** 当前版本预览使用的仓库 ID。 */
  readonly repositoryId: bigint;
  /** 当前版本预览使用的分支 ID；缺省时由后端返回普通分支集合。 */
  readonly branchId?: bigint;
  /** 外部 URL 明确传入的采纳提交 ID，用于稳定标记采纳版本。 */
  readonly adoptedCommitId?: string;
}

export interface CreateVersionPreviewServiceOptions {
  /** 读取当前仓库/分支上下文。 */
  readonly getRepositoryContext: () => VersionPreviewRepositoryContext | null;
  /** 每次拉取的提交记录数量。 */
  readonly pageSize?: number;
}

interface VersionPreviewRecordsCache {
  /** 缓存对应的上下文 key。 */
  readonly key: string;
  /** 当前上下文下的版本记录。 */
  readonly records: readonly VersionPreviewRecord[];
  /** 当前上下文下每个提交推导出的分支 ID，仅供快照加载定位使用。 */
  readonly branchIdByCommitId: ReadonlyMap<string, string>;
}

interface PendingVersionPreviewRecordsRequest {
  /** 正在加载的版本记录上下文 key。 */
  readonly key: string;
  /** 发起该请求时使用的取消信号；不同 signal 代表不同生命周期 owner，不能互相复用。 */
  readonly signal?: AbortSignal;
  /** 当前上下文对应的版本记录加载任务。 */
  readonly promise: Promise<VersionPreviewLoadedRecords>;
}

interface VersionPreviewLoadedRecords {
  /** 当前上下文下的版本记录。 */
  readonly records: readonly VersionPreviewRecord[];
  /** 当前上下文下每个提交推导出的分支 ID，仅供快照加载定位使用。 */
  readonly branchIdByCommitId: ReadonlyMap<string, string>;
}

interface VersionPreviewCommitHistory {
  /** 已按后端分页顺序完整加载的提交记录。 */
  readonly commits: readonly CommitInfo[];
  /** 已按分支 ID 去重的分支信息。 */
  readonly branches: readonly BranchInfo[];
}

/**
 * 将 protobuf Timestamp 转成页面消费的 ISO 字符串。
 *
 * @param timestamp 后端返回的 protobuf 时间。
 * @returns ISO 时间字符串；缺失或非法时返回空字符串。
 */
function timestampToIsoString(timestamp: CommitInfo['createdAt']): string {
  if (!timestamp) {
    return '';
  }

  const milliseconds = Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1_000_000);
  const date = new Date(milliseconds);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

/**
 * 为当前版本预览上下文生成缓存 key。
 *
 * @param projectId 当前项目 ID。
 * @param context 当前仓库/分支上下文。
 * @returns 缓存 key。
 */
function createVersionPreviewContextKey(
  projectId: string,
  context: VersionPreviewRepositoryContext,
): string {
  return [
    projectId.trim(),
    context.repositoryId.toString(),
    context.branchId?.toString() ?? 'all-normal',
    context.adoptedCommitId?.trim() ?? '',
  ].join(':');
}

/**
 * 解析当前页面应使用的普通分支信息。
 *
 * @param branches ListCommits 返回的分支信息。
 * @param context 当前仓库/分支上下文。
 * @returns 最匹配的普通分支信息。
 */
function resolveActiveNormalBranch(
  branches: readonly BranchInfo[],
  context: VersionPreviewRepositoryContext,
): BranchInfo | undefined {
  if (context.branchId !== undefined) {
    const matchedBranch = branches.find((branch) => branch.branchId === context.branchId);
    if (matchedBranch) {
      return matchedBranch;
    }
  }

  return branches.find((branch) => branch.branchKind === BranchKind.NORMAL) ?? branches[0];
}

/**
 * 解析版本列表展示用分支名。
 *
 * @param branch 当前分支信息。
 * @param context 当前仓库/分支上下文。
 * @returns 可展示的分支名。
 */
function resolveVersionPreviewBranchName(
  branch: BranchInfo | undefined,
  context: VersionPreviewRepositoryContext,
): string {
  const branchName = branch?.branchName.trim() ?? '';
  if (branchName) {
    return branchName;
  }

  return context.branchId !== undefined ? context.branchId.toString() : '普通分支';
}

/**
 * 归一化分支 ID，避免无效分支进入 OpenProject 快照定位。
 *
 * @param branch 分支信息。
 * @returns 有效分支 ID；缺失或非正数时返回 undefined。
 */
function normalizeVersionPreviewBranchId(branch: BranchInfo | undefined): string | undefined {
  if (!branch || branch.branchId <= 0n) {
    return undefined;
  }

  return branch.branchId.toString();
}

/**
 * 解析当前采纳版本的提交 ID。
 *
 * @param context 当前仓库/分支上下文。
 * @returns 可用于标记采纳版本的提交 ID。
 */
function resolveAdoptedCommitId(
  context: VersionPreviewRepositoryContext,
): string {
  const adoptedCommitId = context.adoptedCommitId?.trim() ?? '';
  // 采纳版本只能由外部打开链接声明；缺省时不从分支 head 或当前显示提交猜测。
  return adoptedCommitId;
}

/**
 * 将后端 CommitInfo 映射为版本预览列表记录。
 *
 * @param commit 后端提交信息。
 * @param branch 推导出的提交所属分支。
 * @param context 当前仓库/分支上下文。
 * @param adoptedCommitId 当前采纳提交 ID。
 * @returns 页面版本记录。
 */
function toVersionPreviewRecord(
  commit: CommitInfo,
  branch: BranchInfo | undefined,
  context: VersionPreviewRepositoryContext,
  adoptedCommitId: string,
): VersionPreviewRecord {
  const title = commit.message.trim();

  return {
    id: commit.commitId,
    title: title || commit.commitId,
    authorName: commit.createdBy.trim(),
    committedAt: timestampToIsoString(commit.createdAt),
    branchName: resolveVersionPreviewBranchName(branch, context),
    isAdoptedVersion: Boolean(adoptedCommitId) && commit.commitId === adoptedCommitId,
  };
}

/**
 * 将分页返回的提交追加到结果集中，并按提交 ID 去重。
 *
 * 后端分页在并发提交时可能出现边界重复；service 边界先去重，避免 UI 图谱出现重复节点。
 *
 * @param target 当前累积的提交列表。
 * @param seenCommitIds 已追加的提交 ID 集合。
 * @param commits 当前页提交列表。
 */
function appendUniqueCommits(
  target: CommitInfo[],
  seenCommitIds: Set<string>,
  commits: readonly CommitInfo[],
): void {
  for (const commit of commits) {
    const commitId = commit.commitId.trim();
    if (!commitId || seenCommitIds.has(commitId)) {
      continue;
    }

    seenCommitIds.add(commitId);
    target.push(commit);
  }
}

/**
 * 将分页返回的分支追加到去重表中。
 *
 * @param branchById 当前累积的分支表。
 * @param branches 当前页分支列表。
 */
function appendUniqueBranches(
  branchById: Map<string, BranchInfo>,
  branches: readonly BranchInfo[],
): void {
  for (const branch of branches) {
    const branchId = normalizeVersionPreviewBranchId(branch);
    if (!branchId || branchById.has(branchId)) {
      continue;
    }

    branchById.set(branchId, branch);
  }
}

/**
 * 加载当前版本预览上下文下的全部普通分支提交。
 *
 * 设计说明：版本所属分支不是 CommitInfo 的直接字段，必须拿到完整父链后从各分支 head 回溯推导；
 * 因此这里不能只读第一页，否则跨页的中间提交会缺少分支归属。
 *
 * @param context 当前仓库/分支上下文。
 * @param pageSize 每页拉取数量。
 * @returns 完整提交历史与分支信息。
 */
async function loadAllVersionPreviewCommitHistory(
  context: VersionPreviewRepositoryContext,
  pageSize: number,
  signal?: AbortSignal,
): Promise<VersionPreviewCommitHistory> {
  const commits: CommitInfo[] = [];
  const seenCommitIds = new Set<string>();
  const branchById = new Map<string, BranchInfo>();
  let page = 1;

  while (true) {
    const request = create(ListCommitsRequestSchema, {
      repositoryId: context.repositoryId,
      // 版本预览需要拿到仓库下所有普通分支提交，才能根据各分支 head 沿父链回溯提交归属；
      // 这里不能传 branchId，否则后端会把结果限制在当前分支，跨分支历史会缺失。
      branchKind: BranchKind.NORMAL,
      page,
      limit: pageSize,
    });
    const response = signal
      ? await versionClient.listCommits(request, { signal })
      : await versionClient.listCommits(request);

    // 分页加载必须保持后端排序顺序；去重只处理边界重复，不重新排序。
    appendUniqueCommits(commits, seenCommitIds, response.commits);
    appendUniqueBranches(branchById, response.branches);

    const total = Math.max(0, response.total);
    if (response.commits.length === 0) {
      break;
    }
    if (total > 0 && commits.length >= total) {
      break;
    }
    if (response.commits.length < pageSize && total === 0) {
      break;
    }

    page += 1;
  }

  return {
    commits,
    branches: Array.from(branchById.values()),
  };
}

/**
 * 构建提交 ID 到提交信息的查询表。
 *
 * @param commits 完整提交历史。
 * @returns 提交 ID 查询表。
 */
function buildCommitInfoById(commits: readonly CommitInfo[]): Map<string, CommitInfo> {
  const commitById = new Map<string, CommitInfo>();

  for (const commit of commits) {
    const commitId = commit.commitId.trim();
    if (!commitId || commitById.has(commitId)) {
      continue;
    }

    commitById.set(commitId, commit);
  }

  return commitById;
}

/**
 * 从指定分支 head 沿父提交链回溯，收集该分支可达提交。
 *
 * @param branch 分支信息。
 * @param commitById 完整提交查询表。
 * @returns 分支 head 可达的提交 ID 集合。
 */
function collectReachableCommitIdsFromBranch(
  branch: BranchInfo,
  commitById: ReadonlyMap<string, CommitInfo>,
): Set<string> {
  const reachableCommitIds = new Set<string>();
  const stack = [branch.headCommitId.trim()].filter((commitId) => commitId.length > 0);

  while (stack.length > 0) {
    const commitId = stack.pop();
    if (!commitId || reachableCommitIds.has(commitId)) {
      continue;
    }

    reachableCommitIds.add(commitId);
    const commit = commitById.get(commitId);
    if (!commit) {
      continue;
    }

    for (const parentCommitId of commit.parentCommitIds) {
      const normalizedParentCommitId = parentCommitId.trim();
      if (normalizedParentCommitId && !reachableCommitIds.has(normalizedParentCommitId)) {
        stack.push(normalizedParentCommitId);
      }
    }
  }

  return reachableCommitIds;
}

/**
 * 构建每个分支可达的提交集合。
 *
 * @param branches 后端返回的分支信息。
 * @param commitById 完整提交查询表。
 * @returns 分支 ID 到可达提交集合的映射。
 */
function buildReachableCommitIdsByBranchId(
  branches: readonly BranchInfo[],
  commitById: ReadonlyMap<string, CommitInfo>,
): Map<string, ReadonlySet<string>> {
  const reachableCommitIdsByBranchId = new Map<string, ReadonlySet<string>>();

  for (const branch of branches) {
    const branchId = normalizeVersionPreviewBranchId(branch);
    if (!branchId || reachableCommitIdsByBranchId.has(branchId)) {
      continue;
    }

    reachableCommitIdsByBranchId.set(
      branchId,
      collectReachableCommitIdsFromBranch(branch, commitById),
    );
  }

  return reachableCommitIdsByBranchId;
}

/**
 * 判断分支是否可达指定提交。
 *
 * @param branch 分支信息。
 * @param commitId 提交 ID。
 * @param reachableCommitIdsByBranchId 分支可达提交集合。
 * @returns 分支可达该提交时返回 true。
 */
function isCommitReachableFromBranch(
  branch: BranchInfo,
  commitId: string,
  reachableCommitIdsByBranchId: ReadonlyMap<string, ReadonlySet<string>>,
): boolean {
  const branchId = normalizeVersionPreviewBranchId(branch);
  if (!branchId) {
    return false;
  }

  return reachableCommitIdsByBranchId.get(branchId)?.has(commitId) === true;
}

/**
 * 解析提交所属的展示分支。
 *
 * 多分支共同祖先天然可能同时被多个分支可达；这里优先当前打开分支，其次精确命中 head 的分支，
 * 最后按后端返回分支顺序选择第一个可达分支，保证同一批数据下结果稳定。
 *
 * @param commit 提交信息。
 * @param branches 后端返回的分支信息。
 * @param context 当前仓库/分支上下文。
 * @param activeBranch 当前上下文最匹配的普通分支。
 * @param reachableCommitIdsByBranchId 分支可达提交集合。
 * @returns 解析出的分支；无法解析时返回 activeBranch 兜底。
 */
function resolveVersionPreviewBranchForCommit(
  commit: CommitInfo,
  branches: readonly BranchInfo[],
  context: VersionPreviewRepositoryContext,
  activeBranch: BranchInfo | undefined,
  reachableCommitIdsByBranchId: ReadonlyMap<string, ReadonlySet<string>>,
): BranchInfo | undefined {
  const commitId = commit.commitId.trim();
  const contextBranch = context.branchId !== undefined
    ? branches.find((branch) => branch.branchId === context.branchId)
    : undefined;

  if (
    contextBranch
    && isCommitReachableFromBranch(contextBranch, commitId, reachableCommitIdsByBranchId)
  ) {
    return contextBranch;
  }

  const headMatchedBranch = branches.find((branch) => branch.headCommitId.trim() === commitId);
  if (headMatchedBranch) {
    return headMatchedBranch;
  }

  return branches.find((branch) => (
    isCommitReachableFromBranch(branch, commitId, reachableCommitIdsByBranchId)
  )) ?? activeBranch;
}

/**
 * 将完整提交历史转换为版本预览记录和内部快照定位索引。
 *
 * @param history 完整提交历史。
 * @param context 当前仓库/分支上下文。
 * @returns 页面记录与提交到分支 ID 的内部索引。
 */
function buildVersionPreviewLoadedRecords(
  history: VersionPreviewCommitHistory,
  context: VersionPreviewRepositoryContext,
): VersionPreviewLoadedRecords {
  const activeBranch = resolveActiveNormalBranch(history.branches, context);
  const adoptedCommitId = resolveAdoptedCommitId(context);
  const commitById = buildCommitInfoById(history.commits);
  const reachableCommitIdsByBranchId = buildReachableCommitIdsByBranchId(
    history.branches,
    commitById,
  );
  const branchIdByCommitId = new Map<string, string>();
  const records = history.commits.map((commit) => {
    const branch = resolveVersionPreviewBranchForCommit(
      commit,
      history.branches,
      context,
      activeBranch,
      reachableCommitIdsByBranchId,
    );
    const branchId = normalizeVersionPreviewBranchId(branch);
    if (branchId) {
      branchIdByCommitId.set(commit.commitId, branchId);
    }

    return toVersionPreviewRecord(commit, branch, context, adoptedCommitId);
  });

  return {
    records,
    branchIdByCommitId,
  };
}

/**
 * 创建版本预览真实接口服务。
 *
 * 设计说明：
 * 1. 版本预览只展示普通分支，因此 ListCommits 固定传 `BranchKind.NORMAL`；
 * 2. ListCommits 不传 branchId，保证完整加载普通分支提交图后再在前端推导提交归属；
 * 3. 历史快照只携带根节点重载定位，模型文件读取交给整项目切换函数统一处理；
 * 4. 转换逻辑集中在 service 边界，页面和 controller 不直接消费 proto 字段。
 *
 * @param options 创建选项。
 * @returns 版本预览服务。
 */
export function createVersionPreviewService(
  options: CreateVersionPreviewServiceOptions,
): VersionPreviewService {
  const pageSize = Math.max(1, Math.floor(options.pageSize ?? DEFAULT_VERSION_PREVIEW_PAGE_SIZE));
  let recordsCache: VersionPreviewRecordsCache | null = null;
  let pendingRecordsRequest: PendingVersionPreviewRecordsRequest | null = null;

  /**
   * 读取并校验当前仓库上下文。
   *
   * @returns 可用于 ListCommits 的仓库上下文。
   */
  function resolveRepositoryContext(): VersionPreviewRepositoryContext {
    const context = options.getRepositoryContext();
    if (!context) {
      throw new Error('版本预览缺少仓库上下文，无法调用 ListCommits');
    }
    return context;
  }

  /**
   * 从后端加载版本记录。
   *
   * @param projectId 当前项目 ID。
   * @returns 版本记录列表。
   */
  async function loadVersionRecordsPayload(
    projectId: string,
    requestOptions: FetchVersionRecordsOptions = {},
  ): Promise<VersionPreviewLoadedRecords> {
    const context = resolveRepositoryContext();
    const cacheKey = createVersionPreviewContextKey(projectId, context);
    if (recordsCache?.key === cacheKey) {
      return {
        records: recordsCache.records,
        branchIdByCommitId: recordsCache.branchIdByCommitId,
      };
    }
    if (
      pendingRecordsRequest?.key === cacheKey
      && pendingRecordsRequest.signal === requestOptions.signal
      && !pendingRecordsRequest.signal?.aborted
    ) {
      return await pendingRecordsRequest.promise;
    }

    const pendingRequest: PendingVersionPreviewRecordsRequest = {
      key: cacheKey,
      signal: requestOptions.signal,
      promise: (async () => {
        const history = await loadAllVersionPreviewCommitHistory(
          context,
          pageSize,
          requestOptions.signal,
        );
        const loadedRecords = buildVersionPreviewLoadedRecords(history, context);
        recordsCache = {
          key: cacheKey,
          records: loadedRecords.records,
          branchIdByCommitId: loadedRecords.branchIdByCommitId,
        };
        return loadedRecords;
      })(),
    };
    pendingRecordsRequest = pendingRequest;

    try {
      return await pendingRequest.promise;
    } finally {
      if (pendingRecordsRequest === pendingRequest) {
        pendingRecordsRequest = null;
      }
    }
  }

  /**
   * 从后端加载版本记录。
   *
   * @param projectId 当前项目 ID。
   * @returns 版本记录列表。
   */
  async function loadVersionRecords(
    projectId: string,
    requestOptions: FetchVersionRecordsOptions = {},
  ): Promise<readonly VersionPreviewRecord[]> {
    const loadedRecords = await loadVersionRecordsPayload(projectId, requestOptions);
    return loadedRecords.records;
  }

  /**
   * 获取项目版本记录。
   *
   * @param projectId 项目 ID。
   * @returns 版本记录列表。
   */
  async function fetchVersionRecords(
    projectId: string,
    requestOptions: FetchVersionRecordsOptions = {},
  ): Promise<readonly VersionPreviewRecord[]> {
    return await loadVersionRecords(projectId, requestOptions);
  }

  /**
   * 获取指定版本的加载快照。
   *
   * @param projectId 项目 ID。
   * @param versionId 版本 ID。
   * @returns 版本加载快照。
   */
  async function fetchVersionSnapshot(
    projectId: string,
    versionId: string,
    requestOptions: FetchVersionSnapshotOptions = {},
  ): Promise<VersionPreviewSnapshot> {
    const context = resolveRepositoryContext();
    const loadedRecords = await loadVersionRecordsPayload(projectId, requestOptions);
    const records = loadedRecords.records;
    const record = records.find((item) => item.id === versionId);
    if (!record) {
      throw new Error(`版本不存在：projectId=${projectId}, versionId=${versionId}`);
    }
    const branchId = loadedRecords.branchIdByCommitId.get(record.id)
      ?? context.branchId?.toString();

    return {
      projectId,
      versionId: record.id,
      title: record.title,
      branchName: record.branchName,
      modelSource: {
        kind: 'project-root-snapshot',
        repositoryId: context.repositoryId.toString(),
        ...(branchId ? { branchId } : {}),
        commitId: record.id,
      },
    };
  }

  /**
   * 释放版本预览服务持有的记录缓存。
   */
  function dispose(): void {
    recordsCache = null;
    pendingRecordsRequest = null;
  }

  return {
    fetchVersionRecords,
    fetchVersionSnapshot,
    dispose,
  };
}
